import { debounce } from 'lodash';
import React, {
    createContext,
    useContext,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useLocation } from 'react-router';
import RemoveIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { TextField } from '@mui/material';
import IconButton from '@mui/material/IconButton';

export interface SearchStringProps {
    searchString?: string;
    InputComponent: () => React.ReactNode | null;
}

export const SearchStringContext = createContext<SearchStringProps>({
    searchString: undefined,
    InputComponent: () => null,
} as SearchStringProps);

export const SearchStringProvider = ({ children }: React.PropsWithChildren) => {
    const [searchString, setSearchString] = useState<string | undefined>();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const location = useLocation();

    React.useEffect(() => {
        handleClear();
    }, [location]);

    const handleClear = () => {
        if (inputRef.current) {
            inputRef.current.value = '';
        }
        setSearchString(undefined);
    };

    const InputComponent = useMemo(() => {
        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            const { value } = e.target;
            setSearchString(value ? value : undefined);
        };

        const debouncedSubmit = debounce(handleChange, 500);

        return function InputComponent() {
            return (
                <TextField
                    label="Search"
                    size="small"
                    onChange={debouncedSubmit}
                    inputRef={inputRef}
                    defaultValue={searchString || ''}
                    slotProps={{
                        input: {
                            'aria-label': 'Search',
                            endAdornment: (
                                <IconButton onClick={handleClear} sx={{ p: 0 }}>
                                    <RemoveIcon fontSize="small" />
                                </IconButton>
                            ),
                            startAdornment: <SearchIcon fontSize="medium" />,
                        },
                    }}
                />
            );
        };
    }, [searchString]);

    return (
        <SearchStringContext.Provider
            value={{
                searchString,
                InputComponent,
            }}
        >
            {children}
        </SearchStringContext.Provider>
    );
};

export const useSearchString = () => useContext(SearchStringContext);
